import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type { ProjectFileKindEnum } from '../generated/types';

const DIARY_ENTRY_FIELDS = `
  id
  date
  description
  tag {
    id
    name
    slug
  }
  status
  weather
  author {
    id
    fullName
  }
  projectFiles {
    id
    name
    url
    size
    fileType
    contentType
    mediaType
    visibility
  }
  projectFileMetadata {
    countsByMediaType {
      mediaType
      count
    }
  }
  createdAt
  updatedAt
`;

const SITE_INSPECTION_FIELDS = `
  id
  sequence
  displayLabel
  diaryEntries {
    ${DIARY_ENTRY_FIELDS}
  }
`;

export const PROJECT_SITE_INSPECTIONS_QUERY = gql`
  query ProjectSiteInspections($projectId: ID!, $page: Int, $limit: Int) {
    projectSiteInspections(projectId: $projectId, page: $page, limit: $limit) {
      nodes {
        ${SITE_INSPECTION_FIELDS}
      }
      pagination {
        currentPage
        nextPage
        prevPage
        totalPages
        totalCount
        limit
        from
        to
      }
    }
  }
`;

export interface TagItem {
  id: string;
  name: string;
  slug: string;
}

export interface DiaryEntryProjectFile {
  id: string;
  name: string;
  url?: string | null;
  size?: number | null;
  fileType?: ProjectFileKindEnum | null;
  contentType?: string | null;
  mediaType?: string | null;
  visibility?: string | null;
}

export interface FileCountByMediaType {
  mediaType: string;
  count: number;
}

export interface ProjectFileMetadata {
  countsByMediaType: FileCountByMediaType[];
}

export interface DiaryEntryForSiteInspection {
  id: string;
  date: string;
  description: string;
  tag?: TagItem | null;
  status: string;
  weather?: string | null;
  author: { id: string; fullName: string };
  projectFiles?: DiaryEntryProjectFile[] | null;
  projectFileMetadata?: ProjectFileMetadata | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiteInspectionItem {
  id: string;
  sequence: number;
  displayLabel: string;
  diaryEntries: DiaryEntryForSiteInspection[];
}

export interface SiteInspectionsPagination {
  currentPage: number;
  nextPage: number | null;
  prevPage: number | null;
  totalPages: number;
  totalCount: number;
  limit: number;
  from: number | null;
  to: number | null;
}

const DEFAULT_PAGE_SIZE = 20;

export interface UseProjectSiteInspectionsOptions {
  pageSize?: number;
}

export function useProjectSiteInspections(
  projectId: string,
  options: UseProjectSiteInspectionsOptions = {}
) {
  const { pageSize = DEFAULT_PAGE_SIZE } = options;

  const { data, loading, error, refetch, fetchMore } = useQuery(
    PROJECT_SITE_INSPECTIONS_QUERY,
    {
      variables: { projectId, page: 1, limit: pageSize },
      skip: !projectId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: false,
    }
  );

  const connection = (data as any)?.projectSiteInspections;
  const siteInspections: SiteInspectionItem[] = connection?.nodes ?? [];
  const pagination: SiteInspectionsPagination | undefined = connection?.pagination;
  const hasNextPage = pagination?.nextPage != null;

  const loadMore = async () => {
    if (pagination?.nextPage == null) return;
    await fetchMore({
      variables: {
        projectId,
        page: pagination.nextPage,
        limit: pageSize,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const prevConnection = (prev as any)?.projectSiteInspections;
        const nextConnection = (fetchMoreResult as any)?.projectSiteInspections;
        if (!prev || !nextConnection) return prev ?? {};
        return {
          ...prev,
          projectSiteInspections: {
            ...nextConnection,
            nodes: [...(prevConnection?.nodes ?? []), ...(nextConnection?.nodes ?? [])],
          },
        };
      },
    });
  };

  return {
    siteInspections,
    loading,
    error,
    refetch,
    hasNextPage: hasNextPage ?? false,
    loadMore,
    pagination: pagination ?? null,
  };
}
